import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import shrinkImage from '../../utils/shrinkImage';

const BLANK_FORM = { name: '', title: '', bio: '', photo_url: '', sort_order: 0 };

export default function AdminEmployees() {
  const [employees, setEmployees] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // uuid of employee being edited, or 'new'
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = () => api.get('/employees/admin/all').then(r => setEmployees(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEdit = (emp) => { setEditing(emp.uuid); setForm({ name: emp.name, title: emp.title, bio: emp.bio || '', photo_url: emp.photo_url || '', sort_order: emp.sort_order }); setMsg(''); };
  const startNew = () => { setEditing('new'); setForm({ ...BLANK_FORM, sort_order: employees.length + 1 }); setMsg(''); };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const data = new FormData(); data.append('image', await shrinkImage(file));
    try { const r = await api.post('/upload/image', data, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }); set('photo_url', r.data.url); } catch (err) { setMsg(`Photo upload failed: ${err.response?.data?.error || err.message}`); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.name || !form.title) { setMsg('Name and title are required'); return; }
    setSaving(true); setMsg('');
    try {
      if (editing === 'new') {
        await api.post('/employees', form);
      } else {
        await api.put(`/employees/${editing}`, form);
      }
      setEditing(null);
      setMsg('Saved!');
      load();
    } catch { setMsg('Save failed'); }
    setSaving(false);
  };

  const setActive = async (emp, active) => {
    setMsg('');
    try {
      await api.put(`/employees/${emp.uuid}`, { active });
      setEmployees(prev => prev.map(e => e.uuid === emp.uuid ? { ...e, active: active ? 1 : 0 } : e));
    } catch { setMsg('Update failed'); }
  };

  const visible = employees.filter(e => showInactive || e.active);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2744', marginBottom: 2 }}>Team</h1>
          <p style={{ color: '#9a9a9a', fontSize: 13 }}>Manage employee profiles shown on the public website</p>
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#666', cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show removed employees
          </label>
          <button className="btn btn-primary btn-sm" onClick={startNew}>+ Add Employee</button>
        </div>
      </div>
      {msg && <div className={`alert ${msg === 'Saved!' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: 16 }}>{msg}</div>}

      <div className="grid-2" style={{ gap: 20 }}>
        {editing === 'new' && (
          <EmployeeForm form={form} set={set} uploading={uploading} handlePhotoUpload={handlePhotoUpload}
            onCancel={() => setEditing(null)} onSave={save} saving={saving} title="New Employee" />
        )}

        {visible.map(emp => (
          <div key={emp.uuid} className="card" style={{ padding: 20, opacity: emp.active ? 1 : 0.6 }}>
            {editing === emp.uuid ? (
              <EmployeeForm form={form} set={set} uploading={uploading} handlePhotoUpload={handlePhotoUpload}
                onCancel={() => setEditing(null)} onSave={save} saving={saving} />
            ) : (
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', background: '#f4f4f4', border: '2px solid #e8e8e8', flexShrink: 0 }}>
                  {emp.photo_url ? <img src={emp.photo_url} alt={emp.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color: '#9a9a9a' }}>👤</div>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, color: '#1a2744', fontSize: 15 }}>
                    {emp.name} {!emp.active && <span style={{ fontSize: 11, fontWeight: 700, color: '#c62828', marginLeft: 6 }}>REMOVED</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#c9a227', fontWeight: 700, marginBottom: 6 }}>{emp.title}</div>
                  {emp.bio ? <p style={{ fontSize: 13, color: '#666', lineHeight: 1.5, marginBottom: 10 }}>{emp.bio}</p> : <p style={{ fontSize: 12, color: '#bbb', fontStyle: 'italic', marginBottom: 10 }}>No bio yet</p>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => startEdit(emp)}>Edit Profile</button>
                    {emp.active ? (
                      <button className="btn btn-ghost btn-sm" style={{ color: '#c62828' }} onClick={() => setActive(emp, false)}>Remove from site</button>
                    ) : (
                      <button className="btn btn-ghost btn-sm" style={{ color: '#2e7d32' }} onClick={() => setActive(emp, true)}>Restore</button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function EmployeeForm({ form, set, uploading, handlePhotoUpload, onCancel, onSave, saving, title }) {
  return (
    <div>
      {title && <h3 style={{ fontWeight: 800, color: '#1a2744', marginBottom: 16 }}>{title}</h3>}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'flex-start' }}>
        <div>
          <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', background: '#f4f4f4', border: '3px solid #c9a227', marginBottom: 8 }}>
            {form.photo_url ? <img src={form.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#9a9a9a' }}>👤</div>}
          </div>
          <label style={{ display: 'block', fontSize: 12, color: '#c9a227', fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
            {uploading ? 'Uploading...' : '📷 Upload Photo'}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} disabled={uploading} />
          </label>
        </div>
        <div style={{ flex: 1 }}>
          <div className="form-group" style={{ marginBottom: 8 }}><label className="form-label" style={{ fontSize: 11 }}>Name</label><input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} /></div>
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label" style={{ fontSize: 11 }}>Title</label><input className="form-control" value={form.title} onChange={e => set('title', e.target.value)} /></div>
        </div>
      </div>
      <div className="form-group"><label className="form-label">Bio</label><textarea className="form-control" rows={4} value={form.bio} onChange={e => set('bio', e.target.value)} placeholder="A short bio for the website..." /></div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </div>
  );
}
